/**
 * Types for the chart-of-accounts infrastructure (backend modules/Accounting, S2a).
 *
 * Resources: /api/accounting-chart (index, show, store), /api/accounting-chart-template
 * (index of active templates), /api/accounting-owner-settings/{owner} (show).
 * A chart is created once per owner and cannot be updated or deleted through the API;
 * switching charts after the first posting is a separate migration process.
 */
import type { OwnerResource } from "../../owner";

export enum ChartOfAccountsTypeEnum {
  Skr03 = "skr03",
  Skr04 = "skr04",
  Custom = "custom",
  /** Read-only chart holding the shared pre-migration accounts. Never a target for new postings. */
  LegacyShared = "legacy_shared",
}

export interface AccountingChartTemplateResource {
  id: string;
  code: ChartOfAccountsTypeEnum;
  year: number;
  version: number;
  title: string;
  active: boolean;
  accounts_count?: number;
}

export interface AccountingChartResource {
  id: string;
  gid: string;
  /** null only for the legacy_shared chart. */
  owner_id: string | null;
  code: ChartOfAccountsTypeEnum;
  template_id: string | null;
  template_year: number | null;
  template_version: number | null;
  title: string;
  locked_at: string | null;
  is_operative: boolean;
  accepts_new_postings: boolean;
  accounts_count?: number;
  created_at: string | null;
  updated_at: string | null;
  owner?: OwnerResource | null;
  template?: AccountingChartTemplateResource | null;
}

/**
 * Accounting settings of one owner. When the owner has no settings yet the backend
 * answers with `is_legacy_mode: true` and null ids: postings keep the pre-S2a
 * behaviour until a chart is bound.
 */
export interface AccountingOwnerSettingsResource {
  id?: string;
  owner_id: string;
  active_chart_id: string | null;
  legacy_chart_id: string | null;
  chart_migration_date?: string | null;
  open_item_migration_completed_at?: string | null;
  is_open_item_migration_pending?: boolean;
  is_legacy_mode?: boolean;
  active_chart?: AccountingChartResource | null;
  legacy_chart?: AccountingChartResource | null;
}

export type StoreAccountingChartRequestBody = {
  owner_id: string;
  code: ChartOfAccountsTypeEnum;
  /** Required unless code is `custom`. */
  template_id?: string | null;
  /** Required for `custom`, optional override otherwise. */
  title?: string | null;
};
